import { useEffect, useMemo, useRef, useState } from 'react';
import { useUserSearch } from '../../users/hooks/useUsers';
import { useGangLifecycleStore } from '../stores/gangLifecycleStore';
import { GangParticipant } from '../types/gangCreationTypes';

export interface GangSearchUser {
  id: string;
  fullName: string;
  email: string;
  roles?: string[];
}

interface UseGangMemberSelectionOptions {
  selectedIds: string[];
  participants: Record<string, GangParticipant>;
  onSelectUser: (user: GangSearchUser) => void;
  excludeUserIds?: string[];
  maxSelections?: number;
}

interface UseGangMemberSelectionResult {
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  users: GangSearchUser[];
  isLoading: boolean;
  searchError?: Error;
  shouldPreloadMore: boolean;
  isPreloadingMore: boolean;
  showPreloadIndicator: boolean;
  canLoadMore: boolean;
  handleLoadMore: () => void;
  canSelectMore: boolean;
  handleSelectUser: (user: GangSearchUser) => void;
  selectedParticipants: GangParticipant[];
}

export const useGangMemberSelection = ({
  selectedIds,
  participants,
  onSelectUser,
  excludeUserIds,
  maxSelections,
}: UseGangMemberSelectionOptions): UseGangMemberSelectionResult => {
  const { cacheUser } = useGangLifecycleStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [displayedCount, setDisplayedCount] = useState(5);
  const [allFetchedUsers, setAllFetchedUsers] = useState<GangSearchUser[]>([]);
  const [continuationToken, setContinuationToken] = useState<
    string | undefined
  >();
  const previousSearchQueryRef = useRef('');
  const [displayCountCache, setDisplayCountCache] = useState(
    () => new Map<string, number>()
  );
  const [showPreloadIndicator, setShowPreloadIndicator] = useState(false);

  const {
    data: searchResult,
    isLoading,
    error: searchError,
  } = useUserSearch(searchQuery, 20, continuationToken);

  const blockedUserIds = useMemo(() => {
    const blocked = new Set<string>();
    selectedIds.forEach(id => blocked.add(id));
    excludeUserIds?.forEach(id => blocked.add(id));
    return blocked;
  }, [selectedIds, excludeUserIds]);

  useEffect(() => {
    if (searchQuery !== previousSearchQueryRef.current) {
      if (previousSearchQueryRef.current) {
        setDisplayCountCache(
          prev =>
            new Map(prev.set(previousSearchQueryRef.current, displayedCount))
        );
      }

      setContinuationToken(undefined);
      setAllFetchedUsers([]);

      const cachedDisplayCount = displayCountCache.get(searchQuery) || 5;
      setDisplayedCount(cachedDisplayCount);

      previousSearchQueryRef.current = searchQuery;
    }
  }, [searchQuery, displayedCount, displayCountCache]);

  useEffect(() => {
    if (!searchResult) return;

    const filteredUsers = searchResult.users.filter(
      user => !blockedUserIds.has(user.id)
    );

    if (continuationToken) {
      setAllFetchedUsers(prev => [...prev, ...filteredUsers]);
      if (!searchResult.continuationToken) {
        setContinuationToken(undefined);
        setShowPreloadIndicator(false);
      }
    } else {
      setAllFetchedUsers(filteredUsers);
      setShowPreloadIndicator(false);
    }
  }, [searchResult, continuationToken, blockedUserIds]);

  const users = useMemo(
    () => allFetchedUsers.slice(0, displayedCount),
    [allFetchedUsers, displayedCount]
  );

  const remainingLocalUsers = allFetchedUsers.length - displayedCount;

  const shouldPreloadMore =
    remainingLocalUsers <= 5 && Boolean(searchResult?.hasMoreResults);

  const isPreloadingMore = isLoading && Boolean(continuationToken);

  useEffect(() => {
    const hasMoreResults = searchResult?.hasMoreResults;
    const nextToken = searchResult?.continuationToken;

    if (
      remainingLocalUsers <= 5 &&
      hasMoreResults &&
      nextToken &&
      !continuationToken &&
      !isLoading
    ) {
      setShowPreloadIndicator(true);
      setContinuationToken(nextToken);
    }
  }, [remainingLocalUsers, searchResult, continuationToken, isLoading]);

  const handleLoadMore = () => {
    const hasMoreLocalResults = displayedCount < allFetchedUsers.length;

    if (hasMoreLocalResults) {
      const newCount = Math.min(displayedCount + 5, allFetchedUsers.length);
      setDisplayedCount(newCount);
      setDisplayCountCache(prev => new Map(prev.set(searchQuery, newCount)));
      return;
    }

    if (searchResult?.hasMoreResults && searchResult?.continuationToken) {
      setContinuationToken(searchResult.continuationToken);
    }
  };

  const handleSelectUser = (user: GangSearchUser) => {
    cacheUser({
      id: user.id,
      fullName: user.fullName,
      email: user.email,
      roles: user.roles,
    });

    onSelectUser(user);
  };

  const canSelectMore =
    !maxSelections || selectedIds.length < (maxSelections ?? Infinity);

  const canLoadMore =
    users.length > 0 &&
    (Boolean(searchResult?.hasMoreResults) || remainingLocalUsers > 0);

  const selectedParticipants = useMemo(
    () =>
      selectedIds
        .map(userId => participants[userId])
        .filter((participant): participant is GangParticipant =>
          Boolean(participant)
        ),
    [participants, selectedIds]
  );

  return {
    searchQuery,
    setSearchQuery,
    users,
    isLoading,
    searchError: searchError as Error | undefined,
    shouldPreloadMore,
    isPreloadingMore,
    showPreloadIndicator,
    canLoadMore,
    handleLoadMore,
    canSelectMore,
    handleSelectUser,
    selectedParticipants,
  };
};
